"use client";

import React, { useState } from "react";
import { ArrowRightLeft, CircleCheck, FileSpreadsheet, Ruler, ShieldCheck, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
interface StandardMeshSpec {
  mesh: number;
  micron: number;
  mm: number;
  inch: number;
  openArea: string;
}
const COMMON_SPECS: StandardMeshSpec[] = [{
  mesh: 20,
  micron: 840,
  mm: 0.840,
  inch: 0.0331,
  openArea: "46.2%"
}, {
  mesh: 40,
  micron: 420,
  mm: 0.420,
  inch: 0.0165,
  openArea: "36.0%"
}, {
  mesh: 80,
  micron: 177,
  mm: 0.177,
  inch: 0.0070,
  openArea: "31.4%"
}, {
  mesh: 100,
  micron: 149,
  mm: 0.149,
  inch: 0.0059,
  openArea: "30.3%"
}, {
  mesh: 200,
  micron: 74,
  mm: 0.074,
  inch: 0.0029,
  openArea: "33.6%"
}, {
  mesh: 325,
  micron: 44,
  mm: 0.044,
  inch: 0.0017,
  openArea: "30.0%"
}, {
  mesh: 400,
  micron: 37,
  mm: 0.037,
  inch: 0.0015,
  openArea: "33.1%"
}];
export default function UnitConverter() {
  const {
    lang
  } = useLanguage();

  // Converter 1: Micron / MM / Inches / Mesh
  const [apertureVal, setApertureVal] = useState<string>("100");
  const [apertureUnit, setApertureUnit] = useState<"micron" | "mm" | "inch">("micron");

  // Converter 2: Pressure
  const [pressureVal, setPressureVal] = useState<string>("1");
  const [pressureUnit, setPressureUnit] = useState<"bar" | "psi" | "kpa" | "mpa">("bar");

  // Converter 3: Wire Diameter
  const [wireDiameterVal, setWireDiameterVal] = useState<string>("0.10");
  const [wireUnit, setWireUnit] = useState<"mm" | "inch">("mm");

  // Calculations for Aperture
  const getMicrons = (): number => {
    const num = parseFloat(apertureVal) || 0;
    if (apertureUnit === "micron") return num;
    if (apertureUnit === "mm") return num * 1000;
    if (apertureUnit === "inch") return num * 25400;
    return 0;
  };
  const currentMicron = getMicrons();
  const calculatedMM = (currentMicron / 1000).toFixed(4);
  const calculatedInch = (currentMicron / 25400).toFixed(5);
  const approxMeshCount = currentMicron > 0 ? Math.round(15240 / (currentMicron + 50)) : 0;

  // Calculations for Pressure
  const getBar = (): number => {
    const p = parseFloat(pressureVal) || 0;
    if (pressureUnit === "bar") return p;
    if (pressureUnit === "psi") return p * 0.0689476;
    if (pressureUnit === "kpa") return p * 0.01;
    if (pressureUnit === "mpa") return p * 10;
    return 0;
  };
  const currentBar = getBar();
  const calculatedPSI = (currentBar * 14.5038).toFixed(2);
  const calculatedKPa = (currentBar * 100).toFixed(1);
  const calculatedMPa = (currentBar * 0.1).toFixed(3);

  // Calculations for Wire
  const getWireMM = (): number => {
    const w = parseFloat(wireDiameterVal) || 0;
    if (wireUnit === "mm") return w;
    if (wireUnit === "inch") return w * 25.4;
    return 0;
  };
  const currentWireMM = getWireMM();
  const calculatedWireInch = (currentWireMM / 25.4).toFixed(5);
  const calculatedSWG = currentWireMM > 0 ? Math.max(1, Math.round(36 - currentWireMM * 25)) : "-";
  const copy = {
    badge: pick(lang, {
      en: "Technical Calculation Tools",
      zh: "技术工程换算工具",
      es: "Herramientas técnicas de cálculo",
      de: "Technische Berechnungswerkzeuge",
      fr: "Outils de calcul technique",
      pt: "Ferramentas técnicas de cálculo"
    }),
    h2: pick(lang, {
      en: "Filtration & Mesh Unit Converter",
      zh: "过滤与丝网单位换算器",
      es: "Conversor de unidades de filtración y malla",
      de: "Einheitenumrechner für Filtration und Drahtgewebe",
      fr: "Convertisseur d'unités de filtration et toiles",
      pt: "Conversor de unidades de filtração e telas"
    }),
    lead: pick(lang, {
      en: "Convert mesh aperture ratings, micron dimensions, wire diameters, and pressure metrics for industrial woven wire mesh, filter elements, and sintered media.",
      zh: "在线转换工业编织网、滤芯元件及烧结多孔介质的孔径开孔尺寸、微米精度、丝径规格及流体压差单位。",
      es: "Convierta aberturas de malla, micras, diámetros de alambre y presiones para mallas metálicas, cartuchos y medios sinterizados.",
      de: "Rechnen Sie Maschenweiten, Mikronmaße, Drahtdurchmesser und Druckeinheiten für Drahtgewebe, Filterelemente und Sintermedien um.",
      fr: "Convertissez les ouvertures de mailles, dimensions en microns, diamètres de fils et unités de pression pour toiles, cartouches et médias frittés.",
      pt: "Converta aberturas de malha, dimensões em mícrons, diâmetros de arame e métricas de pressão para telas metálicas, filtros e meios sinterizados."
    }),
    cardTitle: pick(lang, {
      en: "Engineering Unit Calculators",
      zh: "工程单位交互换算",
      es: "Calculadoras de unidades de ingeniería",
      de: "Ingenieur-Einheitenrechner",
      fr: "Calculateurs d'unités d'ingénierie",
      pt: "Calculadoras de unidades de engenharia"
    }),
    cardDesc: pick(lang, {
      en: "Select a conversion category below to convert standard B2B filtration metrics instantly.",
      zh: "在下方选择换算类别，即时转换工业过滤的核心工程指标。",
      es: "Seleccione una categoría a continuación para convertir métricas de filtración al instante.",
      de: "Wählen Sie unten eine Kategorie, um Filtrationskennzahlen sofort umzurechnen.",
      fr: "Sélectionnez une catégorie ci-dessous pour convertir instantanément les métriques de filtration.",
      pt: "Selecione uma categoria abaixo para converter métricas de filtração instantaneamente."
    }),
    tabAperture: pick(lang, {
      en: "Aperture & Micron",
      zh: "孔径与微米",
      es: "Abertura y micras",
      de: "Maschenweite & Mikron",
      fr: "Ouverture et microns",
      pt: "Abertura e mícrons"
    }),
    tabPressure: pick(lang, {
      en: "Pressure Drop",
      zh: "流体压降",
      es: "Pérdida de carga",
      de: "Druckverlust",
      fr: "Perte de charge",
      pt: "Queda de pressão"
    }),
    tabWire: pick(lang, {
      en: "Wire Diameter",
      zh: "丝径厚度",
      es: "Diámetro de alambre",
      de: "Drahtdurchmesser",
      fr: "Diamètre du fil",
      pt: "Diâmetro do arame"
    }),
    labelApertureVal: pick(lang, {
      en: "Aperture Dimension Value",
      zh: "网孔尺寸数值",
      es: "Valor de dimensión de abertura",
      de: "Wert der Maschenabmessung",
      fr: "Valeur d'ouverture",
      pt: "Valor da dimensão da abertura"
    }),
    labelPressureVal: pick(lang, {
      en: "Differential Pressure",
      zh: "差压数值",
      es: "Presión diferencial",
      de: "Differenzdruck",
      fr: "Pression différentielle",
      pt: "Pressão diferencial"
    }),
    labelWireVal: pick(lang, {
      en: "Wire Thickness",
      zh: "编织丝径数值",
      es: "Grosor del alambre",
      de: "Drahtstärke",
      fr: "Épaisseur du fil",
      pt: "Espessura do arame"
    }),
    labelUnit: pick(lang, {
      en: "Unit",
      zh: "单位",
      es: "Unidad",
      de: "Einheit",
      fr: "Unité",
      pt: "Unidade"
    }),
    optMicron: pick(lang, {
      en: "Micron (µm)",
      zh: "微米 (µm)",
      es: "Micras (µm)",
      de: "Mikron (µm)",
      fr: "Microns (µm)",
      pt: "Mícrons (µm)"
    }),
    optMm: pick(lang, {
      en: "Millimeter (mm)",
      zh: "毫米 (mm)",
      es: "Milímetros (mm)",
      de: "Millimeter (mm)",
      fr: "Millimètres (mm)",
      pt: "Milímetros (mm)"
    }),
    optInches: pick(lang, {
      en: "Inches (in)",
      zh: "英寸 (in)",
      es: "Pulgadas (in)",
      de: "Zoll (in)",
      fr: "Pouces (in)",
      pt: "Polegadas (in)"
    }),
    resMicrons: pick(lang, {
      en: "Microns (µm)",
      zh: "微米 (µm)",
      es: "Micras (µm)",
      de: "Mikron (µm)",
      fr: "Microns (µm)",
      pt: "Mícrons (µm)"
    }),
    resMillimeters: pick(lang, {
      en: "Millimeters",
      zh: "毫米 (mm)",
      es: "Milímetros",
      de: "Millimeter",
      fr: "Millimètres",
      pt: "Milímetros"
    }),
    resInches: pick(lang, {
      en: "Inches",
      zh: "英寸 (in)",
      es: "Pulgadas",
      de: "Zoll",
      fr: "Pouces",
      pt: "Polegadas"
    }),
    resApproxMesh: pick(lang, {
      en: "Approx. Mesh",
      zh: "近似目数",
      es: "Malla aprox.",
      de: "Ca. Maschenzahl",
      fr: "Maille approx.",
      pt: "Malha aprox."
    }),
    resApproxSwg: pick(lang, {
      en: "Approx. SWG Gauge",
      zh: "近似 SWG 线规",
      es: "Calibre SWG aprox.",
      de: "Ca. SWG-Drahtlehre",
      fr: "Calibre SWG approx.",
      pt: "Bitola SWG aprox."
    }),
    chartTitle: pick(lang, {
      en: "Standard Wire Mesh Aperture Reference Chart",
      zh: "标准丝网孔径基准对照表",
      es: "Tabla de referencia de abertura de malla estándar",
      de: "Referenztabelle für Standard-Maschenweiten",
      fr: "Tableau de référence d'ouverture des toiles standard",
      pt: "Tabela de referência de abertura de telas padrão"
    }),
    chartDesc: pick(lang, {
      en: "Common ISO 9044 / ASTM E2016 stainless steel woven mesh specifications.",
      zh: "常见 ISO 9044 与 ASTM E2016 不锈钢编织丝网规格标准对照。",
      es: "Especificaciones habituales de malla tejida de acero inoxidable según ISO 9044 y ASTM E2016.",
      de: "Gängige Spezifikationen für Edelstahlgewebe nach ISO 9044 und ASTM E2016.",
      fr: "Spécifications courantes de toiles tissées en acier inoxydable selon ISO 9044 et ASTM E2016.",
      pt: "Especificações comuns de telas de aço inoxidável conforme ISO 9044 e ASTM E2016."
    }),
    thMeshCount: pick(lang, {
      en: "Mesh Count",
      zh: "目数",
      es: "Número de mallas",
      de: "Maschenzahl",
      fr: "Nombre de mailles",
      pt: "Contagem de malha"
    }),
    thApertureUm: pick(lang, {
      en: "Aperture (µm)",
      zh: "孔径 (µm)",
      es: "Abertura (µm)",
      de: "Maschenweite (µm)",
      fr: "Ouverture (µm)",
      pt: "Abertura (µm)"
    }),
    thApertureMm: pick(lang, {
      en: "Aperture (mm)",
      zh: "孔径 (mm)",
      es: "Abertura (mm)",
      de: "Maschenweite (mm)",
      fr: "Ouverture (mm)",
      pt: "Abertura (mm)"
    }),
    thApertureIn: pick(lang, {
      en: "Aperture (in)",
      zh: "孔径 (in)",
      es: "Abertura (in)",
      de: "Maschenweite (in)",
      fr: "Ouverture (in)",
      pt: "Abertura (in)"
    }),
    thOpenArea: pick(lang, {
      en: "Open Area %",
      zh: "开孔率 %",
      es: "Área abierta %",
      de: "Freie Siebfläche %",
      fr: "Surface ouverte %",
      pt: "Área aberta %"
    }),
    sideBadge: pick(lang, {
      en: "B2B Technical Support",
      zh: "B2B 技术工程支持",
      es: "Soporte técnico B2B",
      de: "B2B Technischer Support",
      fr: "Support technique B2B",
      pt: "Suporte técnico B2B"
    }),
    sideTitle: pick(lang, {
      en: "Request Calculation Review",
      zh: "申请工程计算复核",
      es: "Solicitar revisión de cálculos",
      de: "Berechnungsprüfung anfordern",
      fr: "Demander une revue de calcul",
      pt: "Solicitar revisão de cálculos"
    }),
    sideDesc: pick(lang, {
      en: "Have intricate flow rate requirements, high-differential pressure conditions, or non-standard micron ratings?",
      zh: "面临复杂工况流量要求、高差压工作环境或非标截留微米等级？",
      es: "¿Requisitos exigentes de caudal, condiciones de alta presión diferencial o micras no estándar?",
      de: "Haben Sie anspruchsvolle Durchflussanforderungen, hohe Differenzdrücke oder Sonder-Mikronwerte?",
      fr: "Exigences complexes de débit, conditions de haute pression différentielle ou microns hors norme ?",
      pt: "Requisitos complexos de vazão, condições de alta pressão diferencial ou mícrons especiais?"
    }),
    sidePoint1: pick(lang, {
      en: "Verified open area % and flow resistance analysis",
      zh: "复核开孔率百分比与流体流阻水力学分析",
      es: "Análisis verificado de porcentaje de área abierta y resistencia al flujo",
      de: "Geprüfte Siebflächenberechnung und Strömungswiderstandsanalyse",
      fr: "Analyse vérifiée du pourcentage de vide et de résistance d'écoulement",
      pt: "Análise verificada de percentual de área aberta e resistência ao fluxo"
    }),
    sidePoint2: pick(lang, {
      en: "Alloy selection for corrosive or high-temp media",
      zh: "针对腐蚀性或高温介质的耐蚀合金材料建议",
      es: "Selección de aleación para medios corrosivos o alta temperatura",
      de: "Werkstoffberatung für korrosive oder Hochtemperaturmedien",
      fr: "Sélection d'alliages pour fluides corrosifs ou haute température",
      pt: "Seleção de ligas para meios corrosivos ou alta temperatura"
    }),
    sidePoint3: pick(lang, {
      en: "Custom sintered layer structure recommendations",
      zh: "非标多层烧结复合滤网结构层级优化匹配",
      es: "Recomendaciones de estructuras multicapa sinterizadas personalizadas",
      de: "Empfehlungen für kundenspezifische mehrlagige Sinterstrukturen",
      fr: "Recommandations de structures multicouches frittées sur mesure",
      pt: "Recomendações de estruturas sinterizadas multicamadas sob medida"
    }),
    sideCtaBtn: pick(lang, {
      en: "Request Calculation Review",
      zh: "申请工程计算复核",
      es: "Solicitar revisión de cálculo",
      de: "Berechnungsprüfung anfordern",
      fr: "Demander la revue de calcul",
      pt: "Solicitar revisão de cálculo"
    }),
    keyFormulasSideTitle: pick(lang, {
      en: "Key Filtration Formulas",
      zh: "核心过滤计算公式",
      es: "Fórmulas clave de filtración",
      de: "Wichtige Filtrationsformeln",
      fr: "Formules clés de filtration",
      pt: "Fórmulas principais de filtração"
    }),
    aoFormulaName: pick(lang, {
      en: "Open Area Percentage (Ao)",
      zh: "开孔率百分比 (Ao)",
      es: "Porcentaje de área abierta (Ao)",
      de: "Freie Siebfläche (Ao)",
      fr: "Pourcentage de vide (Ao)",
      pt: "Percentual de área aberta (Ao)"
    }),
    aoFormulaVars: pick(lang, {
      en: "w = aperture width, d = wire diameter",
      zh: "w = 孔径宽度, d = 编织丝径",
      es: "w = ancho de abertura, d = diámetro del alambre",
      de: "w = Maschenweite, d = Drahtdurchmesser",
      fr: "w = largeur de maille, d = diamètre du fil",
      pt: "w = largura da abertura, d = diâmetro do arame"
    }),
    meshDefName: pick(lang, {
      en: "Mesh Count Definition",
      zh: "目数定义与孔距",
      es: "Definición del conteo de malla",
      de: "Definition der Maschenzahl",
      fr: "Définition du nombre de mailles",
      pt: "Definição de contagem de malha"
    }),
    meshDefVars: pick(lang, {
      en: "Number of openings per linear inch (25.4mm)",
      zh: "每线性英寸 (25.4 mm) 内的孔洞数量",
      es: "Número de aberturas por pulgada lineal (25.4 mm)",
      de: "Anzahl der Maschen pro linearem Zoll (25,4 mm)",
      fr: "Nombre d'ouvertures par pouce linéaire (25,4 mm)",
      pt: "Número de aberturas por polegada linear (25,4 mm)"
    }),
    isoNoteTitle: pick(lang, {
      en: "ISO 9044 Compliance",
      zh: "符合 ISO 9044 标准",
      es: "Conformidad con ISO 9044",
      de: "Konformität mit ISO 9044",
      fr: "Conformité ISO 9044",
      pt: "Conformidade com ISO 9044"
    }),
    isoNoteBody: pick(lang, {
      en: "All standard specifications listed adhere to international wire cloth manufacturing tolerances and ASTM E2016 verification standards.",
      zh: "列出的所有标准规格均遵循国际编织金属丝网制造公差规范与 ASTM E2016 检验标准。",
      es: "Todas las especificaciones listadas cumplen las tolerancias de fabricación internacionales y normas ASTM E2016.",
      de: "Alle aufgeführten Spezifikationen entsprechen den Fertigungstoleranzen nach internationalen Standards und ASTM E2016.",
      fr: "Toutes les spécifications listées respectent les tolérances de fabrication internationales et les normes ASTM E2016.",
      pt: "Todas as especificações listadas atendem às tolerâncias internacionais de fabricação e normas ASTM E2016."
    })
  };
  return <section id="unitconverter" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        
        {/* Section Header */}
        <Reveal>
          <div className="max-w-[65ch] mb-12">
            <Badge variant="outline" className="bg-muted text-foreground mf-caption font-mono uppercase tracking-wider mb-4 border border-border">
              {copy.badge}
            </Badge>
            <h2 className="mf-h2 text-foreground">
              {copy.h2}
            </h2>
            <p className="mt-4 mf-lead text-muted-foreground">
              {copy.lead}
            </p>
          </div>
        </Reveal>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Interactive Converters Tab Panel (8 Cols) */}
          <div className="lg:col-span-8 space-y-6">
            <Card className="border border-border bg-card">
              <CardHeader className="border-b border-border/60 pb-4">
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <ArrowRightLeft className="size-5 text-primary" />
                  <span>{copy.cardTitle}</span>
                </CardTitle>
                <CardDescription className="mf-caption text-muted-foreground">
                  {copy.cardDesc}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <Tabs defaultValue="aperture" className="w-full">
                  <TabsList className="grid grid-cols-3 mb-6 bg-muted p-1 rounded-lg">
                    <TabsTrigger value="aperture" className="text-sm font-medium">
                      {copy.tabAperture}
                    </TabsTrigger>
                    <TabsTrigger value="pressure" className="text-sm font-medium">
                      {copy.tabPressure}
                    </TabsTrigger>
                    <TabsTrigger value="wire" className="text-sm font-medium">
                      {copy.tabWire}
                    </TabsTrigger>
                  </TabsList>

                  {/* Tab 1: Aperture & Micron */}
                  <TabsContent value="aperture" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="aperture-input" className="text-sm font-medium">
                          {copy.labelApertureVal}
                        </Label>
                        <Input id="aperture-input" type="number" value={apertureVal} onChange={e => setApertureVal(e.target.value)} placeholder="e.g. 100" className="bg-background border-input font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="aperture-unit-trigger" className="text-sm font-medium">
                          {copy.labelUnit}
                        </Label>
                        <Select value={apertureUnit} onValueChange={(val: "micron" | "mm" | "inch") => setApertureUnit(val)}>
                          <SelectTrigger id="aperture-unit-trigger" className="w-full bg-background border-input">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="micron">{copy.optMicron}</SelectItem>
                            <SelectItem value="mm">{copy.optMm}</SelectItem>
                            <SelectItem value="inch">{copy.optInches}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Results Grid */}
                    <div aria-live="polite" className="p-4 rounded-lg bg-muted/60 border border-border grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div data-index={0}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resMicrons}</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{currentMicron.toLocaleString()} µm</span>
                      </div>
                      <div data-index={1}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resMillimeters}</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{calculatedMM} mm</span>
                      </div>
                      <div data-index={2}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resInches}</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{calculatedInch} in</span>
                      </div>
                      <div data-index={3}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resApproxMesh}</span>
                        <span className="text-lg font-mono font-semibold text-primary">~{approxMeshCount} mesh</span>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Tab 2: Pressure Drop */}
                  <TabsContent value="pressure" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="pressure-input" className="text-sm font-medium">
                          {copy.labelPressureVal}
                        </Label>
                        <Input id="pressure-input" type="number" value={pressureVal} onChange={e => setPressureVal(e.target.value)} placeholder="e.g. 1" className="bg-background border-input font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pressure-unit-trigger" className="text-sm font-medium">
                          {copy.labelUnit}
                        </Label>
                        <Select value={pressureUnit} onValueChange={(val: "bar" | "psi" | "kpa" | "mpa") => setPressureUnit(val)}>
                          <SelectTrigger id="pressure-unit-trigger" className="w-full bg-background border-input">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bar">Bar</SelectItem>
                            <SelectItem value="psi">PSI (lbs/in²)</SelectItem>
                            <SelectItem value="kpa">Kilopascal (kPa)</SelectItem>
                            <SelectItem value="mpa">Megapascal (MPa)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Results Grid */}
                    <div aria-live="polite" className="p-4 rounded-lg bg-muted/60 border border-border grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div data-index={0}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">Bar</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{currentBar.toFixed(2)} bar</span>
                      </div>
                      <div data-index={1}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">PSI</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{calculatedPSI} psi</span>
                      </div>
                      <div data-index={2}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">kPa</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{calculatedKPa} kPa</span>
                      </div>
                      <div data-index={3}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">MPa</span>
                        <span className="text-lg font-mono font-semibold text-primary">{calculatedMPa} MPa</span>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Tab 3: Wire Diameter */}
                  <TabsContent value="wire" className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                      <div className="sm:col-span-2 space-y-2">
                        <Label htmlFor="wire-input" className="text-sm font-medium">
                          {copy.labelWireVal}
                        </Label>
                        <Input id="wire-input" type="number" step="0.01" value={wireDiameterVal} onChange={e => setWireDiameterVal(e.target.value)} placeholder="e.g. 0.10" className="bg-background border-input font-mono" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="wire-unit-trigger" className="text-sm font-medium">
                          {copy.labelUnit}
                        </Label>
                        <Select value={wireUnit} onValueChange={(val: "mm" | "inch") => setWireUnit(val)}>
                          <SelectTrigger id="wire-unit-trigger" className="w-full bg-background border-input">
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mm">{copy.optMm}</SelectItem>
                            <SelectItem value="inch">{copy.optInches}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Results Grid */}
                    <div aria-live="polite" className="p-4 rounded-lg bg-muted/60 border border-border grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div data-index={0}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resMillimeters}</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{currentWireMM.toFixed(3)} mm</span>
                      </div>
                      <div data-index={1}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resInches}</span>
                        <span className="text-lg font-mono font-semibold text-foreground">{calculatedWireInch} in</span>
                      </div>
                      <div data-index={2}>
                        <span className="mf-caption text-muted-foreground block font-medium uppercase tracking-wider">{copy.resApproxSwg}</span>
                        <span className="text-lg font-mono font-semibold text-primary">SWG {calculatedSWG}</span>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>

            {/* Quick Reference Specs Table */}
            <Card className="border border-border bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FileSpreadsheet className="size-4 text-primary" />
                  <span>{copy.chartTitle}</span>
                </CardTitle>
                <CardDescription className="mf-caption text-muted-foreground">
                  {copy.chartDesc}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm font-mono border-t border-border">
                    <thead className="bg-muted/70 text-muted-foreground font-sans uppercase mf-caption tracking-wider border-b border-border">
                      <tr>
                        <th className="py-2.5 px-4 font-semibold">{copy.thMeshCount}</th>
                        <th className="py-2.5 px-4 font-semibold">{copy.thApertureUm}</th>
                        <th className="py-2.5 px-4 font-semibold">{copy.thApertureMm}</th>
                        <th className="py-2.5 px-4 font-semibold">{copy.thApertureIn}</th>
                        <th className="py-2.5 px-4 font-semibold">{copy.thOpenArea}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/60">
                      {COMMON_SPECS.map((spec, i) => <tr key={i} data-index={i} className="hover:bg-muted/30 transition-colors">
                          <td className="py-2.5 px-4 font-semibold text-primary">{spec.mesh} mesh</td>
                          <td className="py-2.5 px-4">{spec.micron} µm</td>
                          <td className="py-2.5 px-4">{spec.mm} mm</td>
                          <td className="py-2.5 px-4">{spec.inch} in</td>
                          <td className="py-2.5 px-4 text-foreground font-medium">{spec.openArea}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Side Info & Review Request CTA Panel (4 Cols) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Calculation Review CTA Card */}
            <Card className="border border-primary/20 bg-muted/40">
              <CardHeader className="pb-3">
                <Badge variant="default" className="w-fit mb-1 bg-primary text-primary-foreground">
                  {copy.sideBadge}
                </Badge>
                <CardTitle className="text-xl font-bold text-foreground">
                  {copy.sideTitle}
                </CardTitle>
                <CardDescription className="mf-caption text-muted-foreground leading-relaxed">
                  {copy.sideDesc}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-2">
                <ul className="space-y-2 mf-caption text-foreground">
                  <li className="flex items-start gap-2">
                    <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{copy.sidePoint1}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{copy.sidePoint2}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{copy.sidePoint3}</span>
                  </li>
                </ul>

                <div className="pt-2">
                  <Button asChild variant="default" size="lg" className="w-full font-medium">
                    <Link to="/RequestAQuotePage">
                      <span>{copy.sideCtaBtn}</span>
                      <ChevronRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Formula Reference Card */}
            <Card className="border border-border bg-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Ruler className="size-4 text-primary" />
                  <span>{copy.keyFormulasSideTitle}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 mf-caption">
                <div className="p-3 rounded-md bg-muted/60 border border-border">
                  <p className="font-semibold text-foreground mf-caption uppercase tracking-wide">{copy.aoFormulaName}</p>
                  <p className="font-mono text-muted-foreground mt-1 text-sm">Ao = (w / (w + d))² × 100%</p>
                  <p className="text-muted-foreground mt-1 mf-caption">{copy.aoFormulaVars}</p>
                </div>

                <div className="p-3 rounded-md bg-muted/60 border border-border">
                  <p className="font-semibold text-foreground mf-caption uppercase tracking-wide">{copy.meshDefName}</p>
                  <p className="font-mono text-muted-foreground mt-1 text-sm">Mesh = 25.4 / (w + d)</p>
                  <p className="text-muted-foreground mt-1 mf-caption">{copy.meshDefVars}</p>
                </div>
              </CardContent>
            </Card>

            {/* Quality Note */}
            <div className="p-4 rounded-lg bg-card border border-border flex items-start gap-3">
              <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
              <div className="mf-caption text-muted-foreground leading-relaxed">
                <strong className="font-semibold text-foreground block mb-0.5">{copy.isoNoteTitle}</strong>
                {copy.isoNoteBody}
              </div>
            </div>

          </div>

        </div>

      </div>
    </section>;
}